import { ChevronUpIcon } from '@heroicons/react/outline'
import { useMediaQuery } from '@mantine/hooks'
import CourseInfoKDC from 'components/CourseInfo/CourseInfoKDC'
import CarouselRender from 'components/DetailRender/CarouselRender'
import HTMLRender from 'components/DetailRender/HTMLRender'
import Dialog from 'components/Dialog'
import FAQAccordion from 'components/FAQAccordion'
import dayjs from 'dayjs'
import isSameOfAfter from 'dayjs/plugin/isSameOrAfter'
import CourseHero from 'designs/CourseHero'
import Loading from 'designs/Loading'
import KDTCurriculum from 'features/apply/components/KDTCurriculum'
import KlassDetailNav from 'features/klass/components/KlassDetailNav'
import { useKDCAttrQuery } from 'features/onboarding/queries/useKDCAttrQuery'
import _ from 'lodash'
import { AdditionalList } from 'mocks/kdt'
import { useRouter } from 'next/router'
import { useCallback, useMemo, useRef, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useViewItem } from 'shared/gtm/hooks/useViewItem'
import { useCourse } from 'shared/hooks/useCourse'
import { useCourseFaqList } from 'shared/hooks/useCourseFaqList'
import { useDialog } from 'shared/hooks/useDialog'
import WithHeader from 'shared/layout/WithHeader'
import { AppThunkDispatch, RootState, wrapper } from 'stores'
import { fetchCourseDetailThunk, ICourse } from 'stores/courses'
import { axiosErrorHandler } from 'utils/axiosErrorHandler'

import styles from './kdcDetail.module.scss'

dayjs.extend(isSameOfAfter)

export const getServerSideProps = wrapper.getServerSideProps(async ({ store }) => {
  const dispatch = store.dispatch as AppThunkDispatch
  const { router } = store.getState() as RootState
  const schoolId = router.location.pathname.split('/')[2]
  if (schoolId) {
    try {
      await dispatch(fetchCourseDetailThunk(schoolId))
    } catch (e) {
      axiosErrorHandler(e)
    }
  }
})

const KDCDetail = () => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const ref = useRef<HTMLDivElement>(null)

  const router = useRouter()
  const schoolId = useMemo(() => router.query.schoolId, [router]) as string

  const { data: curEpisode, isLoading: curEpisodeLoading } = useKDCAttrQuery(schoolId)
  const { course: kdcData, isLoading: kdcLoading } = useCourse(schoolId)
  const { courseFAQList } = useCourseFaqList(schoolId)

  const {
    isDialogOpened: isCompleteDialogOpen,
    onDialogOpenHandler: onCompleteDialogOpenHandler,
    onDialogCloseHandler: onCompleteDialogCloseHandler,
  } = useDialog()

  const [openAddition, setOpenAddition] = useState<string | null>(null)

  const handleItemClick = (itemId: string) => {
    setOpenAddition(itemId === openAddition ? null : itemId)
  }

  const onFAQHandler = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'frequently_asked_click',
        content_ids: schoolId,
        ...(sessionStorage.getItem('utmAndGclid') &&
          JSON.parse(sessionStorage.getItem('utmAndGclid') as string)),
      },
    })
  }, [schoolId])
  const onNaviHandler = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'upper_navigator_click',
        content_ids: schoolId,
        ...(sessionStorage.getItem('utmAndGclid') &&
          JSON.parse(sessionStorage.getItem('utmAndGclid') as string)),
      },
    })
  }
  useViewItem(kdcData as ICourse, schoolId)

  return (
    <>
      <WithHeader>
        {kdcLoading && curEpisodeLoading && <Loading />}
        <main ref={ref} style={{ minWidth: 360, marginBottom: isMobile ? 80 : 160 }}>
          {kdcData && (
            <CourseHero
              title={kdcData.title}
              subTitle={kdcData.subtitle as string}
              heroImg={{
                sm: kdcData.bannerBgImgS as string,
                md: kdcData.bannerBgImgM as string,
                lg: kdcData.bannerBgImg as string,
              }}
              textColor={kdcData.bannerTextColor}
            />
          )}
          {/* 모바일 modal */}
          {!_.isEmpty(kdcData) && curEpisode && (
            <CourseInfoKDC
              isMobile
              course={kdcData as ICourse}
              curEpisode={curEpisode[0]}
              onCompleteDialogOpenHandler={onCompleteDialogOpenHandler}
            />
          )}
          <KlassDetailNav
            handler={onNaviHandler}
            isReview={false}
            isCurriculum={!_.isEmpty(kdcData?.curriculum)}
            isFaq={!_.isEmpty(courseFAQList)}
          />
          {/* detail 영역 */}
          <div className="mt-4 max-w-7xl lg:mt-6 lg:px-4 xl:mx-auto xl:px-6">
            <div className="lg:flex lg:flex-row lg:items-start lg:space-x-6">
              {/* 왼쪽 상세 */}
              <div>
                <div
                  id="lecture-intro"
                  style={{ maxWidth: isMobile ? '100%' : 848 }}
                  className={styles.container}>
                  {kdcData && kdcData.description ? (
                    <HTMLRender html={kdcData.description} />
                  ) : (
                    <>
                      {kdcData?.descriptionObjects?.map(({ type, data }, idx) =>
                        type === 'html' ? (
                          <HTMLRender html={data as string} key={`${type}_${idx}`} />
                        ) : (
                          <CarouselRender imgList={data as string[]} key={`${type}_${idx}`} />
                        ),
                      )}
                    </>
                  )}
                </div>
                <div className="mx-4 lg:mx-0">
                  {kdcData && !_.isEmpty(kdcData?.curriculum) && (
                    <div id="curriculum" className="mt-20 lg:mt-30">
                      <header className="flex flex-col lg:flex-row lg:items-end">
                        <h2
                          className="text-xl font-bold lg:text-2xl"
                          style={{ lineHeight: '30px' }}>
                          커리큘럼
                        </h2>
                      </header>
                      <div className="mt-4 lg:mt-6">
                        <KDTCurriculum curriculum={kdcData.curriculum} defaultOpenIdx={0} />
                      </div>
                    </div>
                  )}
                  {!_.isEmpty(courseFAQList) && (
                    <div id="faq" className="mt-20 lg:mt-30">
                      <h2 className="text-xl font-bold lg:text-2xl">자주 묻는 질문</h2>
                      <section className="mt-4 lg:mt-6">
                        <FAQAccordion
                          faqList={courseFAQList}
                          defaultOpenIdx={0}
                          handler={onFAQHandler}
                        />
                      </section>
                    </div>
                  )}
                </div>
              </div>
              {/* 오른쪽 고정되는 부분 */}
              <aside
                className="hidden h-auto shrink-0 lg:sticky lg:block"
                style={{ top: 138, width: 360 }}>
                {!_.isEmpty(kdcData) && curEpisode && (
                  <CourseInfoKDC
                    course={kdcData as ICourse}
                    curEpisode={curEpisode[0]}
                    onCompleteDialogOpenHandler={onCompleteDialogOpenHandler}
                  />
                )}
                {/* 부가 설명 */}
                <div className="mb-6 flex flex-col gap-y-2 lg:mt-5">
                  {AdditionalList['kdc'].map(addition => {
                    const isOpen = openAddition === addition.id
                    return (
                      <section
                        className="cursor-pointer rounded-lg bg-gray-50 px-4 py-3"
                        key={addition.id}
                        onClick={() => handleItemClick(addition.id)}>
                        <div className="flex items-center justify-between gap-x-2">
                          <div className="flex items-center gap-x-2">
                            <div className="flex h-6 w-6 items-center justify-center">
                              <img src={addition.icon} alt={addition.title} />
                            </div>
                            <p className="text-sm font-bold">
                              {addition.boldTitle}
                              <span className="ml-1 inline-block font-normal">
                                {addition.title}
                              </span>
                            </p>
                          </div>
                          <ChevronUpIcon
                            className={`${isOpen ? '' : 'rotate-180 transform'} w-4 text-gray-400`}
                          />
                        </div>
                        {/* open */}
                        {isOpen && (
                          <div className="mt-4 flex flex-col gap-y-1.5">
                            {addition.summary && (
                              <p className="text-sm font-semibold">{addition.summary}</p>
                            )}
                            <p className="text-sm text-gray-500">
                              <span className="font-semibold">{addition.boldDesc}</span>
                              {addition.desc}
                            </p>
                            {addition.secondSummary && (
                              <p className="mt-2.5 text-sm font-semibold">
                                {addition.secondSummary}
                              </p>
                            )}
                            {addition.secondDesc && (
                              <p className="text-sm text-gray-500">{addition.secondDesc}</p>
                            )}
                            {addition.reference && (
                              <span className="text-sm text-gray-500">{addition.reference}</span>
                            )}
                          </div>
                        )}
                      </section>
                    )
                  })}
                </div>
              </aside>
            </div>
          </div>
        </main>
      </WithHeader>
      <Dialog
        isOpen={isCompleteDialogOpen}
        onClose={onCompleteDialogCloseHandler}
        title="신청 완료"
        message={
          <div className="mt-4 overflow-y-auto md:mt-3">
            <div className="box-border pb-4 md:pb-5">
              <p className="break-keep text-sm md:text-base">
                최종 수강생이 되셨습니다! <br />
                {dayjs(curEpisode && curEpisode[0]?.startedAt).format('M월 D일')}
                부터 내 강의실에서 학습을 시작할 수 있어요.
              </p>
            </div>
          </div>
        }
        customButton={
          <div className="flex w-full justify-end">
            <button
              onClick={() => router.push('/my/courses')}
              className="rounded-md border border-trueGray-300 px-5 py-3 text-sm font-semibold">
              내 강의실 가기
            </button>
          </div>
        }
      />
    </>
  )
}

export default KDCDetail
